import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  EMPTY_PROGRESS,
  thrownDetail,
  upcomingEvents,
  type Channel,
  type Credentials,
  type SportEventView,
  type VodItem,
  type WatchProgress,
} from '@player/domain';
import { AggregatorScreen } from '@player/ui';
import type { AggregatorOnDemandRef } from '@player/ports';
import type { Composition } from './composition.js';
import type { CatalogCache } from './catalogCache.js';

/**
 * Il contenuto di un aggregatore, chiesto al gateway.
 *
 * Una chiamata sola, all'apertura: `GET /api/v1/app/aggregators/:id` è **l'unica rotta dell'app che
 * calcola** — dipende da cosa è in diretta adesso e da cosa questa linea vede — quindi non si
 * archivia e non si ricontrolla a scadenza.
 *
 * Se fallisce si mostra il codice, non un elenco vuoto: una pagina vuota è indistinguibile da un
 * aggregatore senza contenuti, cioè manda a cercare il guasto nel pannello.
 */

export interface AggregatorHostProps {
  readonly composition: Composition;
  readonly cache: CatalogCache;
  readonly credentials: Credentials;
  readonly aggregatorId: string;
  readonly title: string;
  readonly color: string;
  /** Il listino, per risolvere gli identificativi dei canali in nomi e loghi. */
  readonly channels: ReadonlyArray<Channel>;
  readonly onOpenChannel: (channelId: string) => void;
  readonly onOpenVod: (ref: string) => void;
  readonly onExit: () => void;
}

export function AggregatorHost({
  composition,
  cache,
  credentials,
  aggregatorId,
  title,
  color,
  channels,
  onOpenChannel,
  onOpenVod,
  onExit,
}: AggregatorHostProps): ReactNode {
  const [events, setEvents] = useState<ReadonlyArray<SportEventView>>([]);
  const [channelIds, setChannelIds] = useState<ReadonlyArray<string>>([]);
  const [onDemand, setOnDemand] = useState<ReadonlyArray<AggregatorOnDemandRef>>([]);
  const [vod, setVod] = useState<ReadonlyArray<VodItem>>([]);
  const [progress, setProgress] = useState<WatchProgress>(EMPTY_PROGRESS);
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const gateway = useMemo(() => composition.createGateway(), [composition]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErrorCode(null);
    void gateway
      .readAggregator(credentials, aggregatorId)
      .then((result) => {
        if (cancelled) return;
        // `nowMs` è l'istante **del server**: è contro quello che si decide «in diretta».
        setEvents(upcomingEvents(result.events, result.nowMs));
        setChannelIds(result.channels);
        setOnDemand(result.onDemand);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const detail = thrownDetail(error, Date.now());
        setErrorCode(detail.code);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gateway, credentials, aggregatorId]);

  /*
    Il catalogo on demand serve solo per **arricchire** le locandine: titolo e immagine li manda già
    il server. Se non arriva, la fascia si disegna lo stesso con quello che c'è — e non si aspetta
    diecimila film per mostrare tre titoli.
  */
  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      cache.vod(credentials, 'movie', false),
      cache.vod(credentials, 'series', false),
      cache.progress(),
    ])
      .then(([movies, series, stored]) => {
        if (cancelled) return;
        setVod([...movies.items, ...series.items]);
        setProgress(stored);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [cache, credentials]);

  /** Solo i canali che questa linea ha davvero nel listino: gli altri non si potrebbero aprire. */
  const resolved = useMemo(() => {
    const byId = new Map(channels.map((channel) => [channel.id, channel]));
    const out: Channel[] = [];
    for (const id of channelIds) {
      const channel = byId.get(id);
      if (channel !== undefined) out.push(channel);
    }
    return out;
  }, [channels, channelIds]);

  return (
    <AggregatorScreen
      focusPort={composition.focusPort}
      title={title}
      color={color}
      loading={loading}
      errorCode={errorCode}
      events={events}
      channels={resolved}
      onDemand={onDemand}
      vod={vod}
      progress={progress}
      onOpenChannel={onOpenChannel}
      onOpenVod={onOpenVod}
      onExit={onExit}
    />
  );
}
